// JSON-LD (schema.org) builders for TinyPDFs.
//
// We emit a single <script type="application/ld+json" id="tinypdfs-jsonld">
// per page, containing an array of schema entities. Google accepts array
// roots, and a single script keeps DOM churn minimal across SPA nav.
//
// The page-specific ID is distinct from the global one (#tinypdfs-jsonld-global)
// injected in index.html for Organization / WebSite so the global tags survive
// client-side route changes.

const ORIGIN = 'https://tinypdfs.com';
const ORG_NAME = 'TinyPDFs';
const ORG_LOGO = 'https://tinypdfs.com/favicon.svg';

function absolute(url) {
  if (!url) return ORIGIN;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/')) return `${ORIGIN}${url}`;
  return `${ORIGIN}/${url}`;
}

// --- Entity builders -------------------------------------------------------

export function buildOrganization() {
  return {
    '@type': 'Organization',
    '@id': `${ORIGIN}/#organization`,
    name: ORG_NAME,
    url: ORIGIN,
    logo: ORG_LOGO,
    sameAs: [],
  };
}

export function buildWebSite() {
  return {
    '@type': 'WebSite',
    '@id': `${ORIGIN}/#website`,
    name: ORG_NAME,
    url: ORIGIN,
    publisher: { '@id': `${ORIGIN}/#organization` },
    inLanguage: 'en',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${ORIGIN}/pdfs?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}

export function buildBreadcrumb(items) {
  // items: [{ name, url }]
  return {
    '@type': 'BreadcrumbList',
    itemListElement: (items || []).map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: absolute(it.url),
    })),
  };
}

export function buildFaqPage(faqs) {
  if (!faqs || !faqs.length) return null;
  return {
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.a,
      },
    })),
  };
}

export function buildHowTo({ name, description, steps, totalTime }) {
  if (!steps || !steps.length) return null;
  const obj = {
    '@type': 'HowTo',
    name,
    description,
    step: steps.map((s, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: s.name || `Step ${i + 1}`,
      text: s.text,
    })),
  };
  if (totalTime) obj.totalTime = totalTime; // ISO-8601 duration e.g. "PT2M"
  return obj;
}

export function buildSoftwareApplication({ name, url, description, keywords }) {
  return {
    '@type': 'SoftwareApplication',
    name,
    url: absolute(url),
    description,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Any (Web)',
    browserRequirements: 'Requires a modern browser with JavaScript',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
    isAccessibleForFree: true,
    creator: { '@id': `${ORIGIN}/#organization` },
    publisher: { '@id': `${ORIGIN}/#organization` },
    aggregateRating: undefined, // omit until we have real reviews (don't fake it)
    keywords: Array.isArray(keywords) ? keywords.join(', ') : keywords,
  };
}

// --- DOM injection ---------------------------------------------------------

const SCRIPT_ID = 'tinypdfs-jsonld';

export function applyJsonLd(entities) {
  if (typeof document === 'undefined') return;
  const cleaned = (entities || []).filter(Boolean);
  const existing = document.head.querySelector(`script#${SCRIPT_ID}`);
  if (!cleaned.length) {
    if (existing) existing.remove();
    return;
  }
  const payload = {
    '@context': 'https://schema.org',
    '@graph': cleaned,
  };
  const json = JSON.stringify(payload);
  if (existing) {
    if (existing.textContent !== json) existing.textContent = json;
    return;
  }
  const el = document.createElement('script');
  el.type = 'application/ld+json';
  el.id = SCRIPT_ID;
  el.textContent = json;
  document.head.appendChild(el);
}
